import { CasesService } from '@/@types/generated'
import { CaseType } from '@/@types/generated/models/CaseType'
import { createContext, useContext, useEffect, useState } from 'react'

interface CaseTypesContextProps {
  caseTypes: CaseType[]
}

const CaseTypesContext = createContext<CaseTypesContextProps>(undefined!)

export function useCaseTypes() {
  const context = useContext(CaseTypesContext)
  if (context === undefined) {
    throw new Error(
      'useCaseTypes must be used within a CaseTypesContext.Provider route'
    )
  }

  return context
}

export function CaseTypesProvider({ children }: React.PropsWithChildren) {
  // State
  const [loading, setLoading] = useState(true)
  const [caseTypes, setCaseTypes] = useState<CaseType[]>([])

  // Load once
  useEffect(() => {
    setLoading(true)
    const load = async () => {
      try {
        setCaseTypes(await getCaseTypes())
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  // No case yet, show loading
  if (loading) return <div>Retrieving case types...</div>

  return (
    <CaseTypesContext.Provider value={{ caseTypes }}>
      {children}
    </CaseTypesContext.Provider>
  )
}

// Module-level cache and in-flight promise
const getCaseTypes: () => Promise<CaseType[]> = (() => {
  let cache: CaseType[] | null = null
  let inFlight: Promise<CaseType[]> | null = null

  return async () => {
    if (cache) return cache
    if (inFlight) return inFlight

    // Load from API
    inFlight = CasesService.listCaseTypes().then(({ caseTypes }) => {
      cache = caseTypes || []
      inFlight = null
      return cache
    })

    return inFlight
  }
})()
